import * as R from 'ramda'
import {
  type Constant,
  DateUtils,
  LanguageUtils,
  type NamedEntity,
  type Nil,
  PluralForms,
  PluralUtils,
  Utils,
} from '@pbt/pbt-ui-components'

import i18n from '~/locales/i18n'
import type { Variation } from '~/types'
import { parse } from '~/utils'
import { toInteger } from '~/utils/number'

export const getPluralPackageLabel = (
  packageId: string,
  PackageType: NamedEntity[],
) => {
  const packageName = Utils.getConstantName(packageId, PackageType)
  const packageDisplayName = LanguageUtils.getConstantTranslatedName(
    packageId,
    PackageType,
  )
  return packageName !== 'Each'
    ? `${packageDisplayName}(s)`
    : packageDisplayName
}

// NOTE: in order to have unique id:
// singular -> id_1
// plural -> id_X
export const getPriceUnitsIdFromValue = (value: any) =>
  value && value.includes('_') ? value.split('_')[0] : value
export const getPriceUnitsValuedFromId = (id: string, isPlural: boolean) =>
  `${id}_${isPlural ? 'X' : '1'}`
export const isPluralUnit = (value: string) =>
  value && value.split('_')[1] === 'X'
export const isPackagePluralUnit = (
  InventoryPriceUnit: NamedEntity[],
  value: string,
) => {
  const id = Utils.findConstantIdByName('Per X packages', InventoryPriceUnit)
  return value === getPriceUnitsValuedFromId(id, true)
}

export const getVariationKey = (variationId: string = '', index: number = 0) =>
  JSON.stringify({ variationId, index })
export const parseVariationKey = (key: string) => parse(key)

export const getVariation = (
  variationKey: string | Nil,
  variationsMap: Record<string, Variation[]>,
): Variation | undefined => {
  const parsedKey = parseVariationKey(variationKey ?? '')
  if (!parsedKey) {
    return undefined
  }
  const { variationId, index } = parsedKey
  return R.path<Variation | undefined>([variationId, index], variationsMap)
}

export const toggleSelectedVariationsState = (
  variationsMap: Record<string, Variation[]>,
  variation: Variation,
  variationName: string,
  isFoodCatalogEnabled?: boolean,
  isGlobalItem?: boolean,
) => {
  const alreadyExist = Boolean(variationsMap[variation.id])
  const globalKey = isFoodCatalogEnabled
    ? 'globalInventoryVariationMappingId'
    : 'globalVariationId'
  const globalId = variation[globalKey] ?? variation.id

  return alreadyExist
    ? R.omit([variation.id], variationsMap)
    : {
        ...variationsMap,
        [variation.id]: [
          {
            ...R.omit(['id'], variation),
            ...(isGlobalItem ? { [globalKey]: globalId } : {}),
            name: variation.name || variationName,
          },
        ],
      }
}

export const toggleSelectedCustomCompoundState = (
  variationsMap: Record<string, Variation[]>,
  variation: Record<string, string>,
) => {
  const alreadyExist = Boolean(variationsMap[variation.id])
  return alreadyExist
    ? R.omit([variation.id], variationsMap)
    : {
        ...variationsMap,
        [variation.id]: [{ name: i18n.t('Common:CUSTOM_DRUG_COMPOUND') }],
      }
}

export const getVariationIndex = (
  variationKey: string,
  variationsMap: Record<string, Variation[]>,
) => {
  const parsedKey = parseVariationKey(variationKey)
  if (!parsedKey) {
    return undefined
  }
  const { variationId, index: variationIndex } = parsedKey
  const { index, found } = Object.keys(variationsMap).reduce(
    (
      // eslint-disable-next-line @typescript-eslint/no-shadow
      { index, found },
      id,
    ) =>
      !found
        ? id === variationId
          ? { index: index + variationIndex, found: true }
          : { index: index + variationsMap[id].length, found: false }
        : { index, found },
    { index: 0, found: false },
  )
  return found ? index : undefined
}

export const getNextVariationKey = (
  variationKey: string | Nil,
  variationsMap: Record<string, Variation[]>,
) => {
  const parsedKey = parseVariationKey(variationKey ?? '')
  if (!parsedKey?.variationId) {
    return undefined
  }
  const { variationId, index } = parsedKey
  const selectedVariationIds = Object.keys(variationsMap)
  if (variationsMap[variationId]?.length > index + 1) {
    return getVariationKey(variationId, index + 1)
  }

  const currentIdIndex = selectedVariationIds.indexOf(variationId)

  return currentIdIndex < selectedVariationIds.length - 1
    ? getVariationKey(selectedVariationIds[currentIdIndex + 1], 0)
    : undefined
}

export const getPrevVariationKey = (
  variationKey: string | Nil,
  variationsMap: Record<string, Variation[]>,
) => {
  const parsedKey = parseVariationKey(variationKey ?? '')
  if (!parsedKey) {
    return undefined
  }
  const { variationId, index } = parsedKey
  const selectedVariationIds = Object.keys(variationsMap)

  if (index > 0) {
    return getVariationKey(variationId, index - 1)
  }

  const curentIdIndex = selectedVariationIds.indexOf(variationId)
  return curentIdIndex > 0
    ? getVariationKey(
        selectedVariationIds[curentIdIndex - 1],
        variationsMap[selectedVariationIds[curentIdIndex - 1]].length - 1,
      )
    : undefined
}

export const insertVariation = (
  variation: Variation | Nil,
  variationsMap: Record<string, Variation[]>,
  isFoodCatalogEnabled: boolean,
) => {
  if (!variation) {
    return variationsMap
  }
  const id: string =
    variation?.id ||
    variation[
      isFoodCatalogEnabled
        ? 'globalInventoryVariationMappingId'
        : 'globalVariationId'
    ] ||
    ''
  return {
    ...variationsMap,
    [id]: [...(variationsMap[id] || []), variation],
  }
}

export const getVariationsCount = (
  variationsMap: Record<string, Variation[]> = {},
) =>
  Object.values(variationsMap).reduce((acc, arr) => acc + (arr?.length || 0), 0)

export const addOrUpdateVariation = (
  variationKey: string | Nil,
  variation: Variation,
  variationsMap: Record<string, Variation[]>,
): Record<string, Variation[]> => {
  const parsedKey = parseVariationKey(variationKey || '')
  if (!parsedKey) {
    return variationsMap
  }
  const { variationId, index: variationIndex } = parsedKey
  const variationsWithSameId = variationsMap[variationId] || []
  return {
    ...variationsMap,
    [variationId]:
      variationIndex >= variationsWithSameId.length
        ? [...variationsWithSameId, variation]
        : variationsWithSameId.map((item, index) =>
            index === variationIndex ? variation : item,
          ),
  }
}

export const getCommonPackagingLabel =
  (InventoryProductSizeUnit: NamedEntity[], PackageType: NamedEntity[]) =>
  (item: any): string => {
    if (!item) {
      return ''
    }

    const {
      perPackageUnitsId,
      packageTypeId,
      perPackageAmount,
      count,
      saleUnitOfMeasure,
    } = item || {}

    const perPackageUnits = LanguageUtils.getConstantTranslatedName(
      perPackageUnitsId,
      InventoryProductSizeUnit,
    )
    const packageType = LanguageUtils.getConstantTranslatedName(
      packageTypeId,
      PackageType,
    )

    let initialLabel = `${perPackageAmount} ${perPackageUnits}`

    if (saleUnitOfMeasure) {
      initialLabel += ` ${saleUnitOfMeasure}`
    }

    if (count) {
      initialLabel += ` | ${count}`
    }

    if (packageType) {
      initialLabel += `/${packageType}`
    }

    return initialLabel
  }

export const getOnHandAmount =
  (InventoryProductSizeUnit: NamedEntity[], PackageType: NamedEntity[]) =>
  ({
    perPackageUnitsId,
    packageTypeId,
    perPackageAmount,
    onHandAmount,
  }: Partial<Variation> = {}) => {
    const perPackageUnits = LanguageUtils.getConstantTranslatedName(
      perPackageUnitsId,
      InventoryProductSizeUnit,
    )
    const packageType = LanguageUtils.getConstantTranslatedName(
      packageTypeId,
      PackageType,
    )
    const packageAmount =
      perPackageAmount && perPackageAmount !== 0
        ? toInteger(Number(((onHandAmount || 0) / perPackageAmount).toFixed(2)))
        : 0

    return `${packageAmount} ${packageType} (${
      onHandAmount || 0
    } ${perPackageUnits})`
  }

export const getUnitCoefficient = (
  InventoryInStockUnit: NamedEntity[],
  unitId: string,
  perPackageAmount: number,
): number => {
  const packagesUnitId = Utils.findConstantIdByName(
    'Package',
    InventoryInStockUnit,
  )

  return unitId === packagesUnitId ? perPackageAmount : 1
}

export const getMinimalAdjustmentAmountByOnHandAmount = (
  InventoryInStockUnit: NamedEntity[],
  variation: Variation | undefined,
  initialAmount: number,
  initialUnitId: string,
  directedUnitId: string,
): number => {
  const perPackageAmount = variation?.perPackageAmount || 0
  const onHandAmount = variation?.onHandAmount || 0

  const initialCoefficient = getUnitCoefficient(
    InventoryInStockUnit,
    initialUnitId,
    perPackageAmount,
  )
  const directedCoefficient = getUnitCoefficient(
    InventoryInStockUnit,
    directedUnitId,
    perPackageAmount,
  )

  const initialUnits = initialAmount * initialCoefficient

  const minimalUnits =
    onHandAmount < 0 ? initialUnits : initialUnits - onHandAmount

  const packagesUnitId = Utils.findConstantIdByName(
    'Package',
    InventoryInStockUnit,
  )
  const isPackageDirected = packagesUnitId === directedUnitId

  const preparedUnits = minimalUnits / directedCoefficient

  return isPackageDirected
    ? toInteger(preparedUnits)
    : Number(Utils.round(preparedUnits, 4))
}

export const getDirectAdjustmentDelta = (
  InventoryInStockUnit: NamedEntity[],
  variation: Variation | undefined,
  initialAmount: number,
  initialUnitId: string,
  directedAmount: number,
  directedUnitId: string,
  // eslint-disable-next-line max-params
): number => {
  const perPackageAmount = variation?.perPackageAmount || 0
  const onHandAmount = variation?.onHandAmount || 0

  const initialCoefficient = getUnitCoefficient(
    InventoryInStockUnit,
    initialUnitId,
    perPackageAmount,
  )
  const directedCoefficient = getUnitCoefficient(
    InventoryInStockUnit,
    directedUnitId,
    perPackageAmount,
  )

  const initialUnits = initialAmount * initialCoefficient
  const directedUnits = directedAmount * directedCoefficient

  return Number(
    Utils.round(
      (directedUnits - (onHandAmount - initialUnits)) / directedCoefficient,
      4,
    ),
  )
}

export const getVariationWithAddedOnHandAmount = (
  InventoryInStockUnit: NamedEntity[],
  variation: Variation | undefined,
  initialAmount: number,
  initialUnitId: string,
  addAmount: number,
  addUnitId: string,
  // eslint-disable-next-line max-params
): Variation => {
  const perPackageAmount = variation?.perPackageAmount || 0
  const onHandAmount = variation?.onHandAmount || 0

  const initialCoefficient = getUnitCoefficient(
    InventoryInStockUnit,
    initialUnitId,
    perPackageAmount,
  )
  const addCoefficient = getUnitCoefficient(
    InventoryInStockUnit,
    addUnitId,
    perPackageAmount,
  )

  const relativeAdjustment =
    addAmount * addCoefficient - initialAmount * initialCoefficient

  return {
    ...variation,
    onHandAmount: Utils.round(onHandAmount + relativeAdjustment, 4),
  } as Variation
}

export const getReceivedDate = R.pipe(
  R.prop('receivedDate'),
  DateUtils.formatDate,
)
export const getCreationDate = R.pipe(
  R.prop('creationDate'),
  DateUtils.formatDate,
)

export const pluckStorageLocation: (items: any[]) => string[] = R.pipe(
  R.pluck('storageLocationId') as (items: any[]) => string[],
  R.filter(Boolean),
  R.uniq,
)

export enum UomUnits {
  BOX = 'BOX',
  BOTTLE = 'BOTTLE',
  VIAL = 'VIAL',
  TUBE = 'TUBE',
  SYRINGE = 'SYRINGE',
  PATCH = 'PATCH',
  CASE = 'CASE',
  UNIT = 'UNIT',
  TABLET = 'TABLET',
  CAPSULE = 'CAPSULE',
  AMPULE = 'AMPULE',
  AMPOULE = 'AMPOULE',
  PACK = 'PACK',
  TRAY = 'TRAY',
  CARTON = 'CARTON',
  BAG = 'BAG',
  DOSE = 'DOSE',
  CAN = 'CAN',
  CARD = 'CARD',
  CHEW = 'CHEW',
  GALLON = 'GALLON',
  JAR = 'JAR',
  PILL = 'PILL',
  PINT = 'PINT',
  QUART = 'QUART',
  ROLL = 'ROLL',
}

export type UomSingularFormKey = `${UomUnits}_${PluralForms.ONE}`
export type UomPluralFormKey = `${UomUnits}_${PluralForms.OTHER}`

const UNCHANGED_UNITS = new Set([
  'ml',
  'l',
  'mg',
  'g',
  'kg',
  'mcg',
  'µg',
  'iu',
  'oz',
  'lb',
  '%',
  'each',
  'ea',
  'in',
  'ft',
  'mm',
  'cm',
  'm',
])

export const LocalizedSingularUoms: Record<UomSingularFormKey, string> = {
  BOX_ONE: i18n.t('Constants:UOM_UNITS.BOX_ONE'),
  BOTTLE_ONE: i18n.t('Constants:UOM_UNITS.BOTTLE_ONE'),
  VIAL_ONE: i18n.t('Constants:UOM_UNITS.VIAL_ONE'),
  TUBE_ONE: i18n.t('Constants:UOM_UNITS.TUBE_ONE'),
  SYRINGE_ONE: i18n.t('Constants:UOM_UNITS.SYRINGE_ONE'),
  PATCH_ONE: i18n.t('Constants:UOM_UNITS.PATCH_ONE'),
  CASE_ONE: i18n.t('Constants:UOM_UNITS.CASE_ONE'),
  UNIT_ONE: i18n.t('Constants:UOM_UNITS.UNIT_ONE'),
  TABLET_ONE: i18n.t('Constants:UOM_UNITS.TABLET_ONE'),
  CAPSULE_ONE: i18n.t('Constants:UOM_UNITS.CAPSULE_ONE'),
  AMPULE_ONE: i18n.t('Constants:UOM_UNITS.AMPULE_ONE'),
  AMPOULE_ONE: i18n.t('Constants:UOM_UNITS.AMPOULE_ONE'),
  PACK_ONE: i18n.t('Constants:UOM_UNITS.PACK_ONE'),
  TRAY_ONE: i18n.t('Constants:UOM_UNITS.TRAY_ONE'),
  CARTON_ONE: i18n.t('Constants:UOM_UNITS.CARTON_ONE'),
  BAG_ONE: i18n.t('Constants:UOM_UNITS.BAG_ONE'),
  DOSE_ONE: i18n.t('Constants:UOM_UNITS.DOSE_ONE'),
  CAN_ONE: i18n.t('Constants:UOM_UNITS.CAN_ONE'),
  CARD_ONE: i18n.t('Constants:UOM_UNITS.CARD_ONE'),
  CHEW_ONE: i18n.t('Constants:UOM_UNITS.CHEW_ONE'),
  GALLON_ONE: i18n.t('Constants:UOM_UNITS.GALLON_ONE'),
  JAR_ONE: i18n.t('Constants:UOM_UNITS.JAR_ONE'),
  PILL_ONE: i18n.t('Constants:UOM_UNITS.PILL_ONE'),
  PINT_ONE: i18n.t('Constants:UOM_UNITS.PINT_ONE'),
  QUART_ONE: i18n.t('Constants:UOM_UNITS.QUART_ONE'),
  ROLL_ONE: i18n.t('Constants:UOM_UNITS.ROLL_ONE'),
}

export const LocalizedPluralUoms: Record<UomPluralFormKey, string> = {
  BOX_OTHER: i18n.t('Constants:UOM_UNITS.BOX_OTHER'),
  BOTTLE_OTHER: i18n.t('Constants:UOM_UNITS.BOTTLE_OTHER'),
  VIAL_OTHER: i18n.t('Constants:UOM_UNITS.VIAL_OTHER'),
  TUBE_OTHER: i18n.t('Constants:UOM_UNITS.TUBE_OTHER'),
  SYRINGE_OTHER: i18n.t('Constants:UOM_UNITS.SYRINGE_OTHER'),
  PATCH_OTHER: i18n.t('Constants:UOM_UNITS.PATCH_OTHER'),
  CASE_OTHER: i18n.t('Constants:UOM_UNITS.CASE_OTHER'),
  UNIT_OTHER: i18n.t('Constants:UOM_UNITS.UNIT_OTHER'),
  TABLET_OTHER: i18n.t('Constants:UOM_UNITS.TABLET_OTHER'),
  CAPSULE_OTHER: i18n.t('Constants:UOM_UNITS.CAPSULE_OTHER'),
  AMPULE_OTHER: i18n.t('Constants:UOM_UNITS.AMPULE_OTHER'),
  AMPOULE_OTHER: i18n.t('Constants:UOM_UNITS.AMPOULE_OTHER'),
  PACK_OTHER: i18n.t('Constants:UOM_UNITS.PACK_OTHER'),
  TRAY_OTHER: i18n.t('Constants:UOM_UNITS.TRAY_OTHER'),
  CARTON_OTHER: i18n.t('Constants:UOM_UNITS.CARTON_OTHER'),
  BAG_OTHER: i18n.t('Constants:UOM_UNITS.BAG_OTHER'),
  DOSE_OTHER: i18n.t('Constants:UOM_UNITS.DOSE_OTHER'),
  CAN_OTHER: i18n.t('Constants:UOM_UNITS.CAN_OTHER'),
  CARD_OTHER: i18n.t('Constants:UOM_UNITS.CARD_OTHER'),
  CHEW_OTHER: i18n.t('Constants:UOM_UNITS.CHEW_OTHER'),
  GALLON_OTHER: i18n.t('Constants:UOM_UNITS.GALLON_OTHER'),
  JAR_OTHER: i18n.t('Constants:UOM_UNITS.JAR_OTHER'),
  PILL_OTHER: i18n.t('Constants:UOM_UNITS.PILL_OTHER'),
  PINT_OTHER: i18n.t('Constants:UOM_UNITS.PINT_OTHER'),
  QUART_OTHER: i18n.t('Constants:UOM_UNITS.QUART_OTHER'),
  ROLL_OTHER: i18n.t('Constants:UOM_UNITS.ROLL_OTHER'),
}

const extractBaseName = (unitName: string): string =>
  unitName.replace(/\s\((?:s|es)\)|\((?:s|es)\)/gi, '').trim()

const singularNameToUomUnitMap: Map<string, UomUnits> = (() => {
  const map = new Map<string, UomUnits>()
  Object.values(UomUnits).forEach((enumValue) => {
    const singularKey = PluralUtils.getPluralUnitKey<
      UomSingularFormKey,
      UomUnits
    >(enumValue, PluralForms.ONE)
    const singularName = LocalizedSingularUoms[singularKey]
    if (singularName) {
      map.set(singularName.toLowerCase(), enumValue)
    }
  })
  return map
})()

const getUomUnitEnum = (unitName: string): UomUnits | null => {
  const baseName = extractBaseName(unitName)
  return singularNameToUomUnitMap.get(baseName.toLowerCase()) || null
}

const normalizeUnitCase = (unit: string, isUnchangedUnit: boolean): string => {
  if (isUnchangedUnit) {
    return unit
  }
  return unit.toLowerCase()
}

const getTranslatedUom = <TKey extends UomSingularFormKey | UomPluralFormKey>(
  unit: Constant,
  pluralForm: PluralForms,
  localizedUoms: Record<TKey, string>,
): Constant => {
  const baseName = extractBaseName(unit.name)
  const unitLower = baseName.toLowerCase()
  const unitEnum = getUomUnitEnum(unit.name)

  if (UNCHANGED_UNITS.has(unitLower)) {
    return {
      ...unit,
      nameTranslation: baseName,
    }
  }

  if (unitEnum) {
    const key = PluralUtils.getPluralUnitKey<TKey, UomUnits>(
      unitEnum,
      pluralForm,
    )
    return {
      ...unit,
      nameTranslation: localizedUoms[key] || unit.name,
    }
  }

  return {
    ...unit,
    nameTranslation: unit.name,
  }
}

export const getSingularUoms = (units: Constant[]) =>
  R.map(
    (unit) =>
      getTranslatedUom<UomSingularFormKey>(
        unit,
        PluralForms.ONE,
        LocalizedSingularUoms,
      ),
    units,
  )

export const getPluralUoms = (units: Constant[]) =>
  R.map(
    (unit) =>
      getTranslatedUom<UomPluralFormKey>(
        unit,
        PluralForms.OTHER,
        LocalizedPluralUoms,
      ),
    units,
  )

export const getTranslatedUoms = (units: Constant[]) => ({
  singularUoms: getSingularUoms(units),
  pluralUoms: getPluralUoms(units),
})

export const getUomListPluralized = (units: Constant[], amount: number) => {
  const { singularUoms, pluralUoms } = getTranslatedUoms(units)

  return PluralUtils.getPluralForm(amount, {
    fallback: pluralUoms,
    singularOrPlural: pluralUoms,
    plural: pluralUoms,
    singular: singularUoms,
  })
}

export const getOneUomPluralized = (unitName: string, amount: number) => {
  const baseName = extractBaseName(unitName)
  const unitLower = baseName.toLowerCase()
  if (UNCHANGED_UNITS.has(unitLower)) {
    return normalizeUnitCase(baseName, true)
  }

  const unitEnum = getUomUnitEnum(unitName)
  if (unitEnum) {
    const singularKey = PluralUtils.getPluralUnitKey<
      UomSingularFormKey,
      UomUnits
    >(unitEnum, PluralForms.ONE)
    const pluralKey = PluralUtils.getPluralUnitKey<UomPluralFormKey, UomUnits>(
      unitEnum,
      PluralForms.OTHER,
    )

    return normalizeUnitCase(
      PluralUtils.getPluralForm(amount, {
        fallback: LocalizedPluralUoms[pluralKey] || unitName,
        singularOrPlural: LocalizedPluralUoms[pluralKey] || unitName,
        plural: LocalizedPluralUoms[pluralKey] || unitName,
        singular: LocalizedSingularUoms[singularKey] || unitName,
      }),
      false,
    )
  }

  return normalizeUnitCase(
    PluralUtils.getPluralForm(amount, {
      fallback: unitName,
      singularOrPlural: unitName,
      plural: unitName,
      singular: unitName,
    }),
    false,
  )
}

/** Decimal precision for quantity calculations (packages, units) */
const QUANTITY_DECIMAL_PLACES = 2

/**
 * Calculates total units from ordered quantity and per-package amount.
 * Example: 5 bottles (quantity) × 10 mL (per-package amount) = 50 mL (total units)
 */
export const calculateTotalUnits = (
  quantity: number,
  perPackageAmount: number,
): number => {
  const totalUnits = quantity * perPackageAmount
  return Number(Utils.round(totalUnits, QUANTITY_DECIMAL_PLACES))
}

/**
 * Calculates package count from a total quantity and per-package amount.
 * Example: 50 mL (quantity) ÷ 10 mL (per-package amount) = 5 bottles (package count)
 */
export const calculatePackageCount = (
  quantity: number,
  perPackageAmount: number,
): number => {
  if (perPackageAmount === 0) {
    return 0
  }
  const packageCount = quantity / perPackageAmount
  return Number(Utils.round(packageCount, QUANTITY_DECIMAL_PLACES))
}

export interface QuantitySuggestion {
  label: string
  value: number
}

export const getInventoryItemDisplayQuantity = ({
  orderedQuantity,
  perPackageAmount,
  productSizeUnit,
  packageType,
  originalQuantity,
  breakdownStyle = 'full',
}: {
  breakdownStyle?: 'full' | 'simplified'
  orderedQuantity: number
  originalQuantity?: number
  packageType: string
  perPackageAmount: number
  productSizeUnit: string
}): { breakdown: string | null; total: string } => {
  const totalAmount = originalQuantity ?? orderedQuantity * perPackageAmount
  const pluralizedProductUnit = getOneUomPluralized(
    productSizeUnit,
    totalAmount,
  )

  const total = `${totalAmount} ${pluralizedProductUnit}`

  if (perPackageAmount === 1 || perPackageAmount === 0) {
    return { total, breakdown: null }
  }

  const pluralizedPackageType = getOneUomPluralized(
    packageType,
    orderedQuantity,
  )

  if (breakdownStyle === 'simplified') {
    return {
      total,
      breakdown: `${orderedQuantity} ${pluralizedPackageType}`,
    }
  }

  const pluralizedProductUnitForPerPackage = getOneUomPluralized(
    productSizeUnit,
    perPackageAmount,
  )
  const singularPackageType = getOneUomPluralized(packageType, 1)

  const breakdown = i18n.t(
    'Admin:CATALOG.ORDERS.ORDER_ITEM_QUANTITY_BREAKDOWN',
    {
      quantity: orderedQuantity,
      packageType: pluralizedPackageType,
      amount: perPackageAmount,
      unit: pluralizedProductUnitForPerPackage,
      singularPackageType,
    },
  )

  return { total, breakdown }
}

export const generateQuantitySuggestions = (
  orderedQuantity: number,
  perPackageAmount: number,
  productSizeUnit: string,
  packageTypeName: string,
): QuantitySuggestion[] =>
  R.map(
    (packageCount) => {
      const totalAmount = packageCount * perPackageAmount
      const { total, breakdown } = getInventoryItemDisplayQuantity({
        orderedQuantity: packageCount,
        perPackageAmount,
        productSizeUnit,
        packageType: packageTypeName,
        originalQuantity: totalAmount,
        breakdownStyle: 'simplified',
      })

      return {
        value: totalAmount,
        label: breakdown ? `${total} (${breakdown})` : total,
      }
    },
    R.reverse(R.range(1, orderedQuantity + 1)),
  )
